import type { AccountQuery, AuditLogQuery, DictQuery, TransactionQuery } from './types';
import type { SpendDetailsQuery } from './spend-details';
import type { MaterialProcessJobQuery, MaterialQuery } from './material-process';

/**
 * Centralized query-key factories. All useQuery / invalidateQueries MUST go through here.
 *
 * TanStack Query only requires keys to be serializable arrays. The 4-segment
 * shape below — `[domain, entity, operation, ...params]` — is a **project
 * convention** inspired by Kent C. Dodds' "Effective React Query Keys"
 * (https://tkdodds.com/blog/effective-react-query-keys); it is not a TanStack
 * canonical form. Consistency across the codebase matters more than the exact
 * segment count.
 */

export const accountKeys = {
  all: ['ads', 'account'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (q: AccountQuery) => [...accountKeys.lists(), q] as const,
  detail: (accountId: string) => [...accountKeys.all, 'detail', accountId] as const,
  bindings: (accountId: string) => [...accountKeys.detail(accountId), 'bindings'] as const,
  transactions: (accountId: string, q: TransactionQuery) =>
    [...accountKeys.detail(accountId), 'transactions', q] as const,
  stats: () => [...accountKeys.all, 'stats'] as const,
  apps: () => [...accountKeys.all, 'apps'] as const,
  dailySpend: (date: string) => [...accountKeys.all, 'daily-spend', date] as const,
};

export const appsKeys = {
  all: ['ads', 'apps'] as const,
  lists: () => [...appsKeys.all, 'list'] as const,
  list: (q: object = {}) => [...appsKeys.lists(), q] as const,
  operators: (app: string) => [...appsKeys.all, 'operators', app] as const,
  spendSummary: (params: object) => [...appsKeys.all, 'spend-summary', params] as const,
};

export const adPlatformKeys = {
  all: ['ads', 'ad-platform'] as const,
  lists: () => [...adPlatformKeys.all, 'list'] as const,
  list: (q: DictQuery = {}) => [...adPlatformKeys.lists(), q] as const,
};

export const adProviderKeys = {
  all: ['ads', 'ad-provider'] as const,
  lists: () => [...adProviderKeys.all, 'list'] as const,
  list: (q: DictQuery = {}) => [...adProviderKeys.lists(), q] as const,
};

export const transactionKeys = {
  all: ['ads', 'ad-account-transaction'] as const,
  lists: () => [...transactionKeys.all, 'list'] as const,
  list: (q: TransactionQuery = {}) => [...transactionKeys.lists(), q] as const,
};

export const auditLogKeys = {
  all: ['ads', 'audit-log'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (q: AuditLogQuery = {}) => [...auditLogKeys.lists(), q] as const,
};

export const spendDetailsKeys = {
  all: ['ads', 'spend-details'] as const,
  lists: () => [...spendDetailsKeys.all, 'list'] as const,
  list: (q: SpendDetailsQuery = {}) => [...spendDetailsKeys.lists(), q] as const,
};

export const operatorCodeKeys = {
  all: ['ads', 'operator-code'] as const,
  lists: () => [...operatorCodeKeys.all, 'list'] as const,
  list: (params: {
    q?: string;
    deptCode?: string;
    operatorId?: number;
    page?: number;
    size?: number;
    search?: string;
  }) => [...operatorCodeKeys.lists(), params] as const,
  apps: (id: number) => [...operatorCodeKeys.all, 'apps', id] as const,
};

export const operatorKeys = {
  all: ['ads', 'operator'] as const,
  lists: () => [...operatorKeys.all, 'list'] as const,
  list: (params: { q?: string; deptCode?: string; hasCode?: boolean }) =>
    [...operatorKeys.lists(), params] as const,
  details: () => [...operatorKeys.all, 'detail'] as const,
  detail: (id: number) => [...operatorKeys.details(), id] as const,
};

export const materialProcessKeys = {
  all: ['ads', 'material-process'] as const,
  publicKey: () => [...materialProcessKeys.all, 'public-key'] as const,
  materials: () => [...materialProcessKeys.all, 'materials'] as const,
  materialList: (q: MaterialQuery = {}) => [...materialProcessKeys.materials(), q] as const,
  jobs: () => [...materialProcessKeys.all, 'jobs'] as const,
  jobList: (q: MaterialProcessJobQuery = {}) => [...materialProcessKeys.jobs(), q] as const,
};

export const userKeys = {
  info: () => ['prm', 'user-info'] as const,
  departments: () => ['prm', 'departments'] as const,
};
